import { useEffect, useRef, useState } from 'react';
import { CircleNotch, Lock, Check, Warning } from '@phosphor-icons/react';

import type { ManifestSection, ManifestSetting } from '@/lib/settingsApi';
import { readOnlyWording, settingLabel } from '../catalogue';

/**
 * The building blocks every Settings section is made of.
 *
 * One rule governs all of them, and it is the reason they exist rather than each section doing
 * its own thing: A FIELD THE SERVER SAYS IS READ-ONLY RENDERS NO WRITE CONTROL. Not a disabled
 * input inside a form with a Save button — a plain value and a line naming who does control it.
 * A disabled box still invites the attempt, and a person who cannot tell whether a setting is
 * theirs to change stops trusting the whole screen.
 *
 * Nothing here decides who may edit what. `canWrite` arrives on the manifest, already resolved by
 * the server against the registry.
 */

/** Look a setting up within the section the server sent. Absent = not offered to this person. */
export function useSettingLookup(section: ManifestSection) {
  const byKey = new Map(section.settings.map((s) => [s.key, s]));
  return (key: string): ManifestSetting | undefined => byKey.get(key);
}

export function Panel({
  title,
  description,
  children,
  action,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
        </div>
        {action}
      </header>
      {/* A collapsed panel passes no children. Rendering the padded box anyway left a blank
          strip under the header that looked like a broken card. */}
      {children ? <div className="px-5 py-4">{children}</div> : null}
    </section>
  );
}

/** The authority line under a value nobody here may change. */
export function ReadOnlyNote({ setting, grantor }: { setting: ManifestSetting; grantor?: string | null }) {
  if (setting.canWrite) return null;
  return (
    <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
      <Lock className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{readOnlyWording(setting.readOnlyReason ?? 'not-editable', { grantor })}</span>
    </p>
  );
}

/**
 * One row: a label, and either an editable control or the value as it stands.
 *
 * `setting` may be undefined — that means the server did not offer this key to this person, and
 * the row is not rendered at all rather than shown greyed out.
 */
export function FieldRow({
  setting,
  label,
  children,
  readOnlyValue,
  hint,
}: {
  setting: ManifestSetting | undefined;
  label?: string;
  /** The editable control. Rendered only when the server says this person may write. */
  children?: React.ReactNode;
  /** What the value is, for everyone who may read but not write. */
  readOnlyValue?: React.ReactNode;
  hint?: string;
}) {
  if (!setting) return null;
  return (
    <div className="grid gap-1.5 border-b border-border/60 py-3.5 last:border-0 sm:grid-cols-[minmax(0,13rem)_1fr] sm:gap-4">
      <div className="pt-1.5">
        <label className="text-sm font-medium text-foreground">{label ?? settingLabel(setting.key)}</label>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </div>
      <div className="min-w-0">
        {setting.canWrite ? (
          children
        ) : (
          <div className="pt-1.5">
            <div className="text-sm text-foreground">{readOnlyValue ?? <span className="text-muted-foreground">—</span>}</div>
            <ReadOnlyNote setting={setting} />
          </div>
        )}
      </div>
    </div>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled,
  invalid,
  ...rest
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  invalid?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  return (
    <input
      {...rest}
      type={type}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      /*
        `min-w-0 max-w-full appearance-none h-9` — the same fix components/ui/input.tsx already
        carries, which this component never received. A native date or time control has an
        INTRINSIC width from the browser's picker chrome that beats `w-full`, so in Safari the
        input painted outside its own box: on Working hours it covered the word "to" and the
        second field crossed the card's right edge. Dropping the native appearance lets it size
        like any other input. `h-9` is required with it — the height is what collapses when the
        native chrome goes.
      */
      className={`flex h-9 w-full min-w-0 max-w-full appearance-none rounded-lg border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50
        focus:outline-none focus:ring-2 focus:ring-brand-500/20 disabled:opacity-50
        ${invalid ? 'border-red-400 dark:border-red-500/60' : 'border-border focus:border-brand-400'}`}
    />
  );
}

export function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 shrink-0 overflow-hidden rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40
        ${checked ? 'bg-brand-600' : 'bg-muted-foreground/30'}`}
    >
      {/* `left-0.5` pins the horizontal origin at 2px. Without it the thumb had no horizontal
          anchor at all, so it fell back to its STATIC position — and inside a <button>, which
          is text-align:center by default, that resolves to the middle of the track. The ON
          state then translated a further 16px, pushing the thumb past the 36px track and
          `overflow-hidden` sheared it into a crescent. Anchored, the travel is exact:
          off = 2px…18px, on = 18px…34px — a symmetric 2px inset, nothing to clip. */}
      <span
        className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform
          ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * What happened to the thing the person just changed.
 *
 * Shown beside the control that changed rather than as a toast: a toast that has faded cannot
 * answer "did that save?", and this screen is where the question gets asked.
 */
export function SaveIndicator({ state, error }: { state: SaveState; error?: string | null }) {
  if (state === 'idle') return null;
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <CircleNotch className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
        <Check className="h-3 w-3" weight="bold" /> Saved
      </span>
    );
  }
  return (
    <span className="inline-flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
      <Warning className="mt-0.5 h-3 w-3 shrink-0" weight="fill" />
      <span>{error ?? 'Could not save — your change has not been kept.'}</span>
    </span>
  );
}

/** Drives a SaveIndicator: saving → saved → back to idle, or error, which stays put. */
export function useSaveState() {
  const [state, setState] = useState<SaveState>('idle');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  async function run(fn: () => Promise<unknown>): Promise<boolean> {
    clearTimeout(timer.current);
    setState('saving');
    setError(null);
    try {
      await fn();
      setState('saved');
      // An error stays on screen until the next attempt; a success fades, because a permanent
      // green tick beside every field is noise.
      timer.current = setTimeout(() => setState('idle'), 2500);
      return true;
    } catch (err) {
      setState('error');
      setError(messageOf(err));
      return false;
    }
  }

  return { state, error, run };
}

/** The server's own words where it gave them — never a generic "something went wrong". */
export function messageOf(err: unknown): string {
  const response = (err as { response?: { data?: { error?: { message?: string } }; status?: number } }).response;
  const fromServer = response?.data?.error?.message;
  if (fromServer) return fromServer;
  if (response?.status === 403) return 'You are not permitted to change this.';
  if (response?.status === 413) return 'That file is too large.';
  return 'Could not save — your change has not been kept.';
}

export function SectionIntro({ title, description }: { title: string; description: string }) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
      <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
